import { UseQueryOptions, useMutation, useQuery } from 'react-query';
import { HttpError } from '@frontend/fetch';
import { LimitedSchemaMutationOptions, type ContextlessQueryObserverOptions } from '@frontend/react-query-helpers';
import { SchemaFeatureFlags, SchemaIO } from '@frontend/schema';

type MultiFeatureFlagIsEnabledRequest = {
  flagName: string;
  groupIds: string[];
};

/**
 * @deprecated This hook is deprecated. Please use `useScopedAppFlagStore` instead.
 *
 * Helper hook to query multiple feature flag values for a given flag name across multiple group IDs.
 * @param flagName The name of the feature flag to query
 * @param groupIds The list of group IDs to query the feature flag for
 **/
export const useMultiFeatureFlagIsEnabledQuery = (
  { flagName, groupIds }: MultiFeatureFlagIsEnabledRequest,
  opts?: UseQueryOptions<Record<string, boolean>>
) =>
  useQuery({
    queryKey: ['feature-flags', flagName, ...groupIds],
    queryFn: async () => {
      console.warn('`useMultiFeatureFlagIsEnabledQuery` is deprecated. Please use `useScopedAppFlagStore` instead.');
      const res: Record<string, boolean> = {};
      await Promise.all(
        groupIds.map(async (locationId) => {
          const { enabled } = await SchemaFeatureFlags.FlagEnabled(
            { flagName, locationId },
            { headers: { 'Location-Id': locationId }, skipValidation: true }
          );
          res[locationId] = !!enabled;
          return enabled;
        })
      );
      return res;
    },
    ...opts,
  });

export enum AggregationStrategy {
  ANY = 'any',
  ALL = 'all',
}

// Default global aggregation strategy for feature flags is ANY
// Use this map to update the default behavior for specific flags
const DEFAULT_AGGREGATION_STRATEGY: Record<string, AggregationStrategy> = {
  'team-chat-use-stream': AggregationStrategy.ALL,
};

type AggregateFeatureFlagRequest = {
  flagName: string;
  locationIds: string[] | undefined;
  defaultValue?: boolean;
  aggregationStrategy?: AggregationStrategy;
};

/**
 * @deprecated This hook is deprecated. Please use `useScopedAppFlagStore` instead.
 *
 * Helper hook to derive a single feature flag value across multiple locations according to an aggregation strategy.
 * @param flagName The name of the feature flag to query
 * @param locationIds The list of location IDs that are selected (typically obtained from app scope store)
 * @param defaultValue The default feature flag value to return if the query is loading or there are any failures
 * @param aggregationStrategy The aggregation strategy to use when aggregating the feature flag values across multiple locations
 **/
export const useAggregateFeatureFlagQuery = ({
  flagName,
  locationIds,
  defaultValue = false,
  aggregationStrategy = DEFAULT_AGGREGATION_STRATEGY[flagName] || AggregationStrategy.ANY,
}: AggregateFeatureFlagRequest) => {
  const {
    data: flagsByGroupId,
    isLoading,
    isError,
  } = useMultiFeatureFlagIsEnabledQuery(
    {
      flagName,
      groupIds: locationIds ?? [],
    },
    { enabled: !!locationIds?.filter(Boolean)?.length, retry: false }
  );

  const countOfEnabledFlags = Object.values(flagsByGroupId ?? {}).filter((flagValue) => flagValue).length;
  const countOfDisabledFlags = Object.values(flagsByGroupId ?? {}).filter((flagValue) => !flagValue).length;
  const aggregateValue = flagsByGroupId
    ? aggregationStrategy === AggregationStrategy.ANY
      ? countOfEnabledFlags > 0
      : countOfDisabledFlags === 0
    : defaultValue;
  const isMixed = countOfEnabledFlags > 0 && countOfDisabledFlags > 0;

  return {
    isError,
    isLoading,
    aggregateValue: isLoading ? defaultValue : aggregateValue,
    hasMixedValues: isMixed,
    flagsByGroupId: flagsByGroupId ?? {},
  };
};

export type UpdateFlagIO = SchemaIO<(typeof SchemaFeatureFlags)['UpdateFlag']>;

type UseUpdateFlagMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends UpdateFlagIO['input'] = UpdateFlagIO['input']
> = {
  options?: LimitedSchemaMutationOptions<UpdateFlagIO, E, C, never, RequestOverride>;
};

export type SetFlagForLocationIO = SchemaIO<(typeof SchemaFeatureFlags)['SetFlagForLocation']>;

type UseSetFlagForLocationMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetFlagForLocationIO['input'] = SetFlagForLocationIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetFlagForLocationIO, E, C, never, RequestOverride>;
};

export type DeleteFlagIO = SchemaIO<(typeof SchemaFeatureFlags)['DeleteFlag']>;

type UseDeleteFlagMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends DeleteFlagIO['input'] = DeleteFlagIO['input']
> = {
  options?: LimitedSchemaMutationOptions<DeleteFlagIO, E, C, never, RequestOverride>;
};

export type BatchSetFlagForLocationsIO = SchemaIO<(typeof SchemaFeatureFlags)['BatchSetFlagForLocations']>;

type UseBatchSetForLocationsMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends BatchSetFlagForLocationsIO['input'] = BatchSetFlagForLocationsIO['input']
> = {
  options?: LimitedSchemaMutationOptions<BatchSetFlagForLocationsIO, E, C, never, RequestOverride>;
};

export type SetFlagAllIO = SchemaIO<(typeof SchemaFeatureFlags)['SetFlagAll']>;

type UseSetFlagAllMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetFlagAllIO['input'] = SetFlagAllIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetFlagAllIO, E, C, never, RequestOverride>;
};

export type SetFlagPercentageIO = SchemaIO<(typeof SchemaFeatureFlags)['SetFlagPercentage']>;

type useSetFlagPercentageMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetFlagPercentageIO['input'] = SetFlagPercentageIO['input']
> = {
  options?: LimitedSchemaMutationOptions<SetFlagPercentageIO, E, C, never, RequestOverride>;
};

export const featureFlagQueryKeys = {
  location: (locationId: string) => [locationId, 'features'] as const,
};

/**
 * Returns an empty flag list on 4xx error codes so react-query caches the empty result instead of
 * refetching on every mount. Without this, a 4xx'd location keeps hitting the FF backend
 * because `staleTime` doesn't apply to errored queries.
 */
export const getLocationFeatureFlags = async ({ locationId }: { locationId: string }) => {
  try {
    return await SchemaFeatureFlags.ListLocationFlags({ locationId }, { headers: { 'Location-Id': locationId } });
  } catch (err) {
    // cache 4xx errors as false because there's no value in ever retrying those. 500s can be retried.
    if (err instanceof HttpError && err.status >= 400 && err.status < 500) {
      return { flags: [] };
    }
    throw err;
  }
};

export const getFeatureFlags = () => SchemaFeatureFlags.ListFlags({});

export const getLocationsWithFlag = ({ flagName, value }: { flagName: string; value: boolean }) =>
  SchemaFeatureFlags.ListLocationsWithFlag({
    flagName,
    value,
  });

export const useQueryLocationFeatureFlags = (locationId: string, options: ContextlessQueryObserverOptions) => {
  return useQuery({
    queryKey: ['location-feature-flags', locationId],
    queryFn: () => getLocationFeatureFlags({ locationId }),
    select: (data) => data.flags,
    ...options,
  });
};

export const useUpdateFlagMutation = ({ options }: UseUpdateFlagMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: UpdateFlagIO['input']) => SchemaFeatureFlags.UpdateFlag(req),
    ...options,
  });
};

export const useSetFlagForLocationMutation = ({ options }: UseSetFlagForLocationMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: SetFlagForLocationIO['input']) => SchemaFeatureFlags.SetFlagForLocation(req),
    ...options,
  });
};

export const useDeleteFlagMutation = ({ options }: UseDeleteFlagMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: DeleteFlagIO['input']) => SchemaFeatureFlags.DeleteFlag(req),
    ...options,
  });
};

export const useBatchSetForLocationsMutation = ({ options }: UseBatchSetForLocationsMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: BatchSetFlagForLocationsIO['input']) => SchemaFeatureFlags.BatchSetFlagForLocations(req),
    ...options,
  });
};

export const useSetFlagAllMutation = ({ options }: UseSetFlagAllMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: SetFlagAllIO['input']) => SchemaFeatureFlags.SetFlagAll(req),
    ...options,
  });
};

export const useSetFlagPercentageMutation = ({ options }: useSetFlagPercentageMutationArgs = {}) => {
  return useMutation({
    mutationFn: (req: SetFlagPercentageIO['input']) => SchemaFeatureFlags.SetFlagPercentage(req),
    ...options,
  });
};
